import React from 'react';
import { Flex, Typography, Button, Spin, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { prerenderUserPatch } from '../../../../actions/prerenderUserActions';
import { useTrackSetupChecklistEvent } from '~features/SetupChecklist';
import {
  daysSinceIntegration,
  hasReportableProof,
  proofDomain,
  RenderProofScreen,
  useRenderProof,
  useRenderProofExperience,
  useRenderProofExperimentProfile,
  useRenderProofSeen,
  useTrackRenderProofEvent,
} from '~features/render-proof';
import { useSelector } from '../../../../hooks/usePrerenderUser';
import { useGetDomainQuery } from '../../api/integrationWizardSlice';

const { Title, Paragraph, Link, Text } = Typography;

const SUCCESS_IMAGE = '/assets/img/onboarding/integrationSuccessful.svg';

function VerificationSuccessfulStep({ onUpgrade }: { onUpgrade: () => void }) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const trackSetupChecklistEvent = useTrackSetupChecklistEvent();
  const wasLegitimatelyShownRef = React.useRef(false);

  // Labels the account before the arms diverge, so a control-arm customer who
  // never returns to the dashboard is still segmentable.
  useRenderProofExperimentProfile();

  const experience = useRenderProofExperience();
  const isTestExperience = experience === 'test_experience';
  const { data: domainData } = useGetDomainQuery(null);
  const domain = domainData?.domain;
  // Polls while the homepage render queued by verification is still on its way,
  // so this screen turns into the proof on its own instead of the user having to
  // come back to the dashboard later.
  // Whether this account is in the experiment at all. Deliberately free of
  // `isError`, which comes back *from* the query below.
  const isInExperiment = isTestExperience;

  const { proof, isError, isFetching, isStillRendering } = useRenderProof({
    domain,
    skip: !isInExperiment,
  });

  // One gate for everything the proof drives, so the uses cannot drift apart.
  const isRenderProofEligible = isInExperiment && !isError;

  // A pending or errored proof has nothing rendered to show: fall back to the
  // existing success screen rather than an empty comparison.
  const showRenderProof = isRenderProofEligible && hasReportableProof(proof);

  // The proof arrives by swapping this screen out from under the user, which is
  // startling with no warning and invisible to anyone who clicks through in the
  // first few seconds. Saying a scan is running frames the swap — without
  // blocking it: every control below stays live, and this disappears on its own
  // when the render lands or when polling gives up after a minute.
  const isScanning = isRenderProofEligible && isStillRendering;

  const trackRenderProofEvent = useTrackRenderProofEvent();
  // The dashboard entry point is the safety net for accounts this screen could
  // not serve in time. Recording the proof as seen here — account-scoped, so it
  // covers every login on the account — stops that net catching someone who has
  // already been shown it.
  const { markViewed: markProofSeen } = useRenderProofSeen();
  const trackingCodeInstalledDate = useSelector(
    (state) => state.prerenderUser.trackingCodeInstalledDate,
  );
  const proofViewedTrackedRef = React.useRef(false);

  React.useEffect(() => {
    if (!showRenderProof || !proof || proofViewedTrackedRef.current) return;
    proofViewedTrackedRef.current = true;
    markProofSeen();
    trackRenderProofEvent('Integration: Render Proof Viewed', {
      location: 'integration_wizard',
      // Taken from the proof, which is present whenever this fires, rather than
      // the wizard's separately-cached domain, which can be empty.
      domain: proofDomain(proof.url, domain ?? ''),
      render_proof_status: proof.status,
      render_status_code: proof.renderStatusCode,
      days_since_integration: daysSinceIntegration(
        trackingCodeInstalledDate,
        new Date(),
      ),
      render_proof_experience: experience,
    });
  }, [
    showRenderProof,
    proof,
    domain,
    trackingCodeInstalledDate,
    experience,
    trackRenderProofEvent,
    markProofSeen,
  ]);

  // The customer sees the ordinary success screen in this case, by design. The
  // experiment still has to know it happened, or a third of the arm looks like
  // indifference rather than absence.
  const unavailableTrackedRef = React.useRef(false);

  React.useEffect(() => {
    if (!isRenderProofEligible && !isError) return;
    if (showRenderProof || isStillRendering || isFetching) return;
    if (!isError && proof === undefined) return;
    if (unavailableTrackedRef.current) return;

    unavailableTrackedRef.current = true;
    trackRenderProofEvent('Integration: Render Proof Unavailable', {
      location: 'integration_wizard',
      domain: domain ?? '',
      // Both awaiting statuses can only reach this effect once the polling
      // window has closed, so they are timeouts: an `origin_error` seen here is
      // necessarily status-less (one with a status code shows the warning
      // screen instead of landing in this apology).
      reason: isError
        ? 'error'
        : proof?.status === 'pending' || proof?.status === 'origin_error'
          ? 'timeout'
          : 'no_comparable_data',
      days_since_integration: daysSinceIntegration(
        trackingCodeInstalledDate,
        new Date(),
      ),
      render_proof_experience: experience,
    });
  }, [
    isRenderProofEligible,
    isError,
    isFetching,
    isStillRendering,
    showRenderProof,
    proof,
    domain,
    trackingCodeInstalledDate,
    experience,
    trackRenderProofEvent,
  ]);

  const onSkipForNowClicked = () => {
    trackSetupChecklistEvent('SkipForNow');
    navigate('/');
  };

  const onAddSitemapClicked = () => {
    trackRenderProofEvent('Sitemap page viewed', {
      source: 'render_proof',
      source_location: 'integration_wizard',
    });
    navigate('/sitemaps');
  };

  const onTroubleshootStatusCode = () => {
    trackRenderProofEvent('Status codes doc viewed', {
      source: 'render_proof',
      source_location: 'integration_wizard',
    });
  };

  const onGoToDashboardClicked = () => {
    trackRenderProofEvent('Dashboard viewed', {
      source: 'render_proof',
      source_location: 'integration_wizard',
    });
    navigate('/');
  };

  const updateTrackingCodeInstalled = React.useCallback(() => {
    dispatch(prerenderUserPatch({ trackingCodeInstalledDate: new Date(), trackingCodeInstalled: true }));
  }, [dispatch]);

  const onStartOptimizationClicked = () => {
    updateTrackingCodeInstalled();
  };

  React.useEffect(() => {
    // Edge case if the users somehow open this url directly

    // Mark that the component was legitimately shown after a short delay
    // This ensures it wasn't immediately redirected away
    const timer = setTimeout(() => {
      wasLegitimatelyShownRef.current = true;
    }, 500);

    return () => {
      clearTimeout(timer);
      // Only set the flag in cleanup if the component was legitimately shown
      if (wasLegitimatelyShownRef.current) {
        updateTrackingCodeInstalled();
      }
    };
  }, [updateTrackingCodeInstalled]);

  if (showRenderProof) {
    return (
      <RenderProofScreen
        proof={proof}
        onAddSitemap={onAddSitemapClicked}
        onGoToDashboard={onGoToDashboardClicked}
        onTroubleshootStatusCode={onTroubleshootStatusCode}
      />
    );
  }

  return (
    <Flex vertical align="center" justify="center" gap="middle" flex={1}>
      <img src={SUCCESS_IMAGE} alt="success state" style={{ maxHeight: '75px', maxWidth: '75px' }} />
      <Title level={1} style={{ marginBottom: 0 }}>
        Success!
      </Title>
      <div style={{ maxWidth: 560, textAlign: 'center' }}>
        <Paragraph>
          Your Prerender integration is successfully up and running. You&apos;re ready to start rendering for crawlers.
        </Paragraph>
        <div style={{ marginTop: token.marginXXL }}>
          <Title level={3}>Now let&apos;s optimize.</Title>
          <Paragraph>
            Complete a few quick steps to improve your online visibility and make the most out of your account.
          </Paragraph>
        </div>
      </div>
      {isScanning && (
        <Flex align="center" gap={token.marginXS} aria-live="polite">
          <Spin size="small" />
          <Text type="secondary">
            Scanning your domain — we&apos;ll show what crawlers see in a moment.
          </Text>
        </Flex>
      )}

      <Button type="primary" onClick={onStartOptimizationClicked}>
        Start optimization
      </Button>
      <Link style={{ color: token.colorPrimary }} onClick={onSkipForNowClicked}>
        Skip for now
      </Link>
    </Flex>
  );
}

export default VerificationSuccessfulStep;
